import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { UIState, ProductFilters } from '../types';

interface UIStore extends UIState {
  setSidebarOpen: (open: boolean) => void;
  setMessagesSidebarOpen: (open: boolean) => void;
  setConversationsSidebarOpen: (open: boolean) => void;
  setConversationsSidebarProduct: (product: UIState['conversationsSidebarProduct']) => void;
  setSearchQuery: (query: string) => void;
  setChromeHidden: (hidden: boolean) => void;
  addRecentSearch: (query: string) => void;
  removeRecentSearch: (query: string) => void;
  clearRecentSearches: () => void;
  setSelectedFilters: (filters: ProductFilters) => void;
  updateFilter: (key: keyof ProductFilters, value: any) => void;
  clearFilters: () => void;
  setSortBy: (sortBy: UIState['sortBy']) => void;
  setViewMode: (viewMode: UIState['viewMode']) => void;
  resetUI: () => void;
  setPendingConversationTarget: (target: UIState['pendingConversationTarget'] | undefined) => void;
  setJobApplicationWriterMinimized: (minimized: boolean) => void;
  setJobApplicationWriterOpen: (open: boolean) => void;
  setSelectedJobsCount: (count: number) => void;
  addSelectedJob: (jobId: string, jobData?: any) => void;
  removeSelectedJob: (jobId: string) => void;
  isJobSelected: (jobId: string) => boolean;
  clearSelectedJobs: () => void;
  setGeneratingApplication: (jobId: string, generating: boolean) => void;
  isGeneratingApplication: (jobId: string) => boolean;
  setGeneratedApplication: (jobId: string, applicationId: string) => void;
  getGeneratedApplicationId: (jobId: string) => string | null;
  hasGeneratedApplication: (jobId: string) => boolean;
  syncSelectedJobsCount: () => void;
}

const initialFilters: ProductFilters = {};

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
  sidebarOpen: false,
  messagesSidebarOpen: false,
  conversationsSidebarOpen: false,
  conversationsSidebarProduct: undefined,
  searchQuery: '',
  recentSearches: [] as string[],
  chromeHidden: false,
  selectedFilters: initialFilters,
  sortBy: 'newest',
  viewMode: 'grid',
  pendingConversationTarget: undefined,
  jobApplicationWriterMinimized: false,
  jobApplicationWriterOpen: false,
  selectedJobsCount: 0,
  selectedJobs: [] as string[],
  selectedJobsData: [] as any[],
  generatingApplications: {} as { [jobId: string]: boolean },
  generatedApplications: {} as { [jobId: string]: string },
  
  setSidebarOpen: (open: boolean) => {
    set({ sidebarOpen: open });
  },
  
  setMessagesSidebarOpen: (open: boolean) => {
    set({ messagesSidebarOpen: open });
  },
  
  setConversationsSidebarOpen: (open: boolean) => {
    set({ conversationsSidebarOpen: open });
  },
  
  setConversationsSidebarProduct: (product) => {
    set({ conversationsSidebarProduct: product });
  },
  
  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
  },

  setChromeHidden: (hidden: boolean) => {
    set({ chromeHidden: hidden } as any);
  },

  addRecentSearch: (query: string) => {
    const trimmed = (query || '').trim();
    if (!trimmed) return;
    const { recentSearches } = get() as any;
    const maxItems = 15;
    const next = [trimmed, ...recentSearches.filter((q: string) => q.toLowerCase() !== trimmed.toLowerCase())].slice(0, maxItems);
    set({ recentSearches: next } as any);
  },

  removeRecentSearch: (query: string) => {
    const { recentSearches } = get() as any;
    set({ recentSearches: recentSearches.filter((q: string) => q !== query) } as any);
  },

  clearRecentSearches: () => {
    set({ recentSearches: [] } as any);
  },
  
  setSelectedFilters: (filters: ProductFilters) => {
    set({ selectedFilters: filters });
  },
  
  updateFilter: (key: keyof ProductFilters, value: any) => {
    const { selectedFilters } = get();
    set({
      selectedFilters: {
        ...selectedFilters,
        [key]: value,
      },
    });
  },
  
  clearFilters: () => {
    set({ selectedFilters: initialFilters });
  },
  
  setSortBy: (sortBy: UIState['sortBy']) => {
    set({ sortBy });
  },
  
  setViewMode: (viewMode: UIState['viewMode']) => {
    set({ viewMode });
  },
  
  resetUI: () => {
    set({
      sidebarOpen: false,
      messagesSidebarOpen: false,
      conversationsSidebarOpen: false,
      conversationsSidebarProduct: undefined,
      searchQuery: '',
      recentSearches: [],
      selectedFilters: initialFilters,
      sortBy: 'newest',
      viewMode: 'grid',
      pendingConversationTarget: undefined,
    });
  },

  setPendingConversationTarget: (target) => {
    set({ pendingConversationTarget: target });
  },

  setJobApplicationWriterMinimized: (minimized: boolean) => {
    set({ jobApplicationWriterMinimized: minimized });
  },

  setJobApplicationWriterOpen: (open: boolean) => {
    set({ jobApplicationWriterOpen: open });
  },

  setSelectedJobsCount: (count: number) => {
    set({ selectedJobsCount: count });
  },

  addSelectedJob: (jobId: string, jobData?: any) => {
    const { selectedJobs, selectedJobsData } = get();
    console.log('Adding job:', jobId, 'Job data:', jobData, 'Current selected jobs:', selectedJobs);
    if (!selectedJobs.includes(jobId)) {
      const newSelectedJobs = [...selectedJobs, jobId];
      const newSelectedJobsData = jobData ? [...selectedJobsData, jobData] : selectedJobsData;
      console.log('New selected jobs:', newSelectedJobs, 'Count:', newSelectedJobs.length);
      console.log('New selected jobs data:', newSelectedJobsData);
      set({ 
        selectedJobs: newSelectedJobs,
        selectedJobsData: newSelectedJobsData,
        selectedJobsCount: newSelectedJobs.length 
      });
    } else {
      console.log('Job already selected:', jobId);
    }
  },

  removeSelectedJob: (jobId: string) => {
    const { selectedJobs, selectedJobsData } = get();
    console.log('Removing job:', jobId, 'Current selected jobs:', selectedJobs);
    const newSelectedJobs = selectedJobs.filter(id => id !== jobId);
    const newSelectedJobsData = selectedJobsData.filter((job, index) => {
      // Find the job that matches the jobId to remove
      return job.id !== jobId;
    });
    console.log('New selected jobs after removal:', newSelectedJobs, 'Count:', newSelectedJobs.length);
    console.log('New selected jobs data after removal:', newSelectedJobsData);
    set({ 
      selectedJobs: newSelectedJobs,
      selectedJobsData: newSelectedJobsData,
      selectedJobsCount: newSelectedJobs.length 
    });
  },

  isJobSelected: (jobId: string) => {
    const { selectedJobs } = get();
    return selectedJobs.includes(jobId);
  },

  clearSelectedJobs: () => {
    set({ 
      selectedJobs: [], 
      selectedJobsData: [], 
      selectedJobsCount: 0,
      generatingApplications: {},
      generatedApplications: {}
    });
  },

  setGeneratingApplication: (jobId: string, generating: boolean) => {
    const { generatingApplications } = get();
    const newGeneratingApplications = { ...generatingApplications };
    if (generating) {
      newGeneratingApplications[jobId] = true;
    } else {
      delete newGeneratingApplications[jobId];
    }
    set({ generatingApplications: newGeneratingApplications });
  },

  isGeneratingApplication: (jobId: string) => {
    const { generatingApplications } = get();
    return generatingApplications[jobId] || false;
  },

  setGeneratedApplication: (jobId: string, applicationId: string) => {
    const { generatedApplications } = get();
    const newGeneratedApplications = { ...generatedApplications };
    newGeneratedApplications[jobId] = applicationId;
    set({ generatedApplications: newGeneratedApplications });
  },

  getGeneratedApplicationId: (jobId: string) => {
    const { generatedApplications } = get();
    return generatedApplications[jobId] || null;
  },

  hasGeneratedApplication: (jobId: string) => {
    const { generatedApplications } = get();
    return !!generatedApplications[jobId];
  },

  // Synchronize selectedJobsCount with selectedJobs array length
  syncSelectedJobsCount: () => {
    const { selectedJobs } = get();
    set({ selectedJobsCount: selectedJobs.length });
  },
}),
    {
      name: 'ui-store', // unique name for localStorage key
      partialize: (state) => ({
        // Only persist the minimized state and selected jobs
        jobApplicationWriterMinimized: state.jobApplicationWriterMinimized,
        recentSearches: (state as any).recentSearches,
        selectedJobs: state.selectedJobs,
        selectedJobsData: state.selectedJobsData,
        selectedJobsCount: state.selectedJobsCount,
      }),
    }
  )
);
